import React, { useEffect, useMemo, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import { Search, UserPlus, Trash2, Loader2, Link2, Copy, ChevronDown, ChevronRight, Plane, Mail, Upload, Download, FileText, X, AlertTriangle, Check, Eye, ClipboardCheck, Phone, MessageSquare, MapPin } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import Papa from 'papaparse';
import InfoTip from '../../components/InfoTip';
import LateInviteControls from '../../components/guests/LateInviteControls';
import QuickRsvpEntry from '../../components/guests/QuickRsvpEntry';
import AddressFields from '../../components/guests/AddressFields';
import AddressStatusChip from '../../components/guests/AddressStatusChip';
import AddressRequestDialog from '../../components/guests/AddressRequestDialog';
import MealSelect from '../../components/guests/MealSelect';
import ConsentProofButton from '../../components/compliance/ConsentProofButton';
import { addressStatus } from '../../components/guests/addressStatus';
import MessageLog from '../../components/messages/MessageLog';
import PlusOneCheck from '../../components/guests/PlusOneCheck';

const SOURCE_LABEL = {
  called: 'Called us', texted: 'Texted us', emailed: 'Emailed us',
  in_person: 'Told us in person', other: 'Other', auto_reply_by: 'Auto — reply-by passed', late_invite: 'Late invite / reopened',
};

const RELATIONSHIP_OPTIONS = [
  '', 'Mother', 'Father', 'Sister', 'Brother', 'Daughter', 'Son',
  'Grandmother', 'Grandfather', 'Aunt', 'Uncle', 'Cousin', 'Niece', 'Nephew',
  'Stepmother', 'Stepfather', 'Stepsister', 'Stepbrother',
  'Godparent', 'Godchild',
  'Best friend', 'Friend', 'Family friend', 'Neighbour',
  'Colleague', 'Boss', 'Mentor',
  'Bridesmaid', 'Groomsman', 'Maid of honor', 'Best man',
  'Officiant', 'Vendor', 'Other',
];

const rsvpStyles = {
  Yes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  No: 'bg-stone-100 text-stone-600 border-stone-200',
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function Guests({ editGuestId, editNonce }) {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sideFilter, setSideFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [needsEmailOnly, setNeedsEmailOnly] = useState(false);
  const [openedNoReplyOnly, setOpenedNoReplyOnly] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [logRsvpGuest, setLogRsvpGuest] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const [showAddrReq, setShowAddrReq] = useState(false);
  const [addrFilter, setAddrFilter] = useState(false);
  const [mealOptions, setMealOptions] = useState(undefined);
  useEffect(() => { client.get('/rsvp-config').then(({ data }) => setMealOptions(data.mealOptions)).catch(() => {}); }, []);
  const [profileTz, setProfileTz] = useState('');
  const { toast } = useToast();

  const toggleExpanded = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const reloadGuests = async () => {
    const { data } = await client.get('/guests');
    setGuests(data);
    setLoading(false);
  };
  useEffect(() => { reloadGuests(); }, []);

  // Fetch the couple's timezone so RSVP audit + delivery timestamps
  // render in their local time (America/Los_Angeles for you) instead of
  // whatever the current browser happens to be in.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/profile');
        if (data?.timezone) setProfileTz(data.timezone);
      } catch { /* ignore */ }
    })();
  }, []);

  const fmtAuditTs = (iso) => {
    if (!iso) return '';
    try {
      const opts = { dateStyle: 'medium', timeStyle: 'medium' };
      if (profileTz) opts.timeZone = profileTz;
      return new Date(iso).toLocaleString(undefined, opts);
    } catch { return String(iso); }
  };

  // When we're deep-linked via ?edit={guestId} (typically from an Insights
  // drill-down), auto-expand that guest, scroll them into view and briefly
  // ring-highlight the row so the couple immediately sees which record
  // they're being pointed at.
  useEffect(() => {
    if (!editGuestId || loading) return;
    setExpanded((prev) => ({ ...prev, [editGuestId]: true }));
    setHighlightedId(editGuestId);
    // Clear any filters that would hide this guest
    setQuery('');
    setFilter('all');
    setSideFilter('all');
    setGroupFilter('all');
    setNeedsEmailOnly(false);
    // Scroll after render
    setTimeout(() => {
      const el = document.querySelector(`[data-guest-row="${editGuestId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    // Drop the highlight ring after 2.5s
    const t = setTimeout(() => setHighlightedId(null), 2500);
    return () => clearTimeout(t);
  }, [editGuestId, editNonce, loading]);

  const filtered = useMemo(() => guests.filter((g) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || [g.name, g.email, g.phone, g.plusOneName, g.group]
      .some((v) => (v || '').toLowerCase().includes(q));
    const matchF = filter === 'all' ? true : g.rsvp === filter;
    const matchSide = sideFilter === 'all' ? true : g.side === sideFilter;
    const matchGroup = groupFilter === 'all' ? true : g.group === groupFilter;
    const matchNeedsEmail = !needsEmailOnly ? true : (!g.email && !!g.phone);
    // "Opened but hasn't RSVPed" — the email dot went maroon (or we have
    // an opened_at timestamp) AND the guest still shows Pending.
    const isOpenedNoReply = (g.email_status === 'opened' || !!g.email_opened_at) && g.rsvp !== 'Yes' && g.rsvp !== 'No';
    const matchOpenedNoReply = !openedNoReplyOnly ? true : isOpenedNoReply;
    const matchAddr = !addrFilter || ['missing', 'needs_fix'].includes(addressStatus(g));
    return matchQ && matchF && matchSide && matchGroup && matchNeedsEmail && matchOpenedNoReply && matchAddr;
  }), [guests, query, filter, sideFilter, groupFilter, needsEmailOnly, openedNoReplyOnly, addrFilter]);

  // Effective attending flag for a plus-one: use guest.plusOneComing when set,
  // else fall back to guest.plusOne (eligibility) so existing users' headcounts
  // stay the same as before this migration.
  const isPlusOneAttending = (g) => !!g.plusOne && g.rsvp !== 'No' && g.plusOneComing !== false;
  const brideCount = guests.filter(g => g.side === 'Bride').length + guests.filter(g => g.side === 'Bride' && isPlusOneAttending(g)).length;
  const groomCount = guests.filter(g => g.side === 'Groom').length + guests.filter(g => g.side === 'Groom' && isPlusOneAttending(g)).length;
  const bothCount = guests.filter(g => g.side === 'Both').length + guests.filter(g => g.side === 'Both' && isPlusOneAttending(g)).length;

  const updateGuest = async (id, patch) => {
    if (patch.plusOne === false) patch = { ...patch, plusOneComing: false };
    setGuests((prev) => prev.map(g => g.id === id ? { ...g, ...patch } : g));
    try { await client.put(`/guests/${id}`, patch); } catch (e) { toast({ title: 'Could not save that change', description: 'Please try again.', variant: 'destructive' }); reloadGuests(); }
  };

  const cycleRsvp = (g) => {
    const order = ['Yes', 'Pending', 'No'];
    const next = order[(order.indexOf(g.rsvp) + 1) % order.length];
    updateGuest(g.id, { rsvp: next });
  };

  const removeGuest = async (id) => {
    setGuests((prev) => prev.filter(g => g.id !== id));
    try { await client.delete(`/guests/${id}`); } catch (e) { /* ignore */ }
    toast({ title: 'Guest removed' });
  };

  const addGuest = async () => {
    try {
      const { data } = await client.post('/guests', {});
      setGuests((prev) => [data, ...prev]);
    } catch (e) {
      // 402 = plan-cap hit. Surface the message so the couple knows to upgrade.
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && typeof d === 'object') {
        toast({
          title: 'Guest limit reached',
          description: d.message || `You've hit your plan's ${d.limit}-guest limit. Upgrade for more.`,
        });
      } else {
        toast({ title: "Couldn't add guest", description: (typeof d === 'string' ? d : e.message) || 'Try again.' });
      }
    }
  };

  const copyRsvpLink = async (guest) => {
    try {
      const { data } = await client.post(`/guest-portal/issue-link/${guest.id}`);
      const url = `${window.location.origin}${data.path}`;
      await navigator.clipboard.writeText(url);
      toast({ title: 'RSVP link copied', description: `Send it to ${guest.name}.` });
    } catch (e) {
      toast({ title: "Couldn't generate link", description: e?.response?.data?.detail || e.message });
    }
  };

  const generateAllLinks = async () => {
    try {
      const { data } = await client.post('/guest-portal/issue-all');
      toast({ title: `RSVP links ready`, description: `${data.minted} new link${data.minted === 1 ? '' : 's'} generated.` });
      const { data: g } = await client.get('/guests');
      setGuests(g);
    } catch (e) {
      toast({ title: "Couldn't generate links" });
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-[#6A6A6A]"><Loader2 className="w-4 h-4 animate-spin" /> Loading guests…</div>;

  const yes = guests.filter(g => g.rsvp === 'Yes').length;
  const yesWithPlus = guests.filter(g => g.rsvp === 'Yes' && isPlusOneAttending(g)).length;
  const totalInvited = guests.length + guests.filter(g => isPlusOneAttending(g)).length;
  const pending = guests.filter(g => g.rsvp === 'Pending').length;
  const seatsNeeded = yes + yesWithPlus;
  const oot = guests.filter(g => g.outOfTowner).length;
  // Guests we have a phone for but no email yet — they'll get the "reply
  // with your email" SMS, and their email will land here once they respond.
  const needsEmailCount = guests.filter(g => !g.email && !!g.phone).length;
  const openedNoReplyCount = guests.filter(g =>
    (g.email_status === 'opened' || !!g.email_opened_at) && g.rsvp !== 'Yes' && g.rsvp !== 'No'
  ).length;

  return (
    <div className="animate-float-up">
      <h1 className="font-serif text-4xl text-[#2A2A2A] mb-1 flex items-center gap-3"><AppIcon feature="guests" size="lg" reveal testId="section-icon-guests" />Guest list</h1>
      <p className="text-[#6A6A6A] mb-8">Track RSVPs, plus-ones, dietary needs and notes in one organized place.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="Total invited" value={totalInvited} sub={guests.filter(g => isPlusOneAttending(g)).length ? `${guests.length} named + ${guests.filter(g => isPlusOneAttending(g)).length} partner${guests.filter(g => isPlusOneAttending(g)).length === 1 ? '' : 's'}` : ''} />
        <Stat label="Confirmed" value={yes} accent />
        <Stat label="Pending" value={pending} />
        <Stat label="Out of towners" value={oot} />
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat data-testid="stat-bride-side" label="Bride's side" value={brideCount} />
        <Stat data-testid="stat-groom-side" label="Groom's side" value={groomCount} />
        <Stat data-testid="stat-both-side" label="Both / shared" value={bothCount} />
      </div>

      <div className="soft-card rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-[#EFE3D8] flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8A7E] pointer-events-none" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search guests by name" data-testid="guests-search-input" className="w-full pl-9 pr-3 py-2 rounded-full bg-[#FBF7F2] border border-[#EFE3D8] text-sm focus:outline-none focus:border-[#7B2C3B]" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {['all', 'Yes', 'Pending', 'No'].map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f ? 'bg-[#7B2C3B] text-[#FBF7F2] border-[#7B2C3B]' : 'bg-transparent text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}>
                {f === 'all' ? 'All' : f}
              </button>
            ))}
            <button
              data-testid="filter-needs-email"
              onClick={() => setNeedsEmailOnly((v) => !v)}
              title="Show only guests we still need an email for (they have a phone but no email yet)"
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1 ${needsEmailOnly ? 'bg-[#7B2C3B] text-[#FBF7F2] border-[#7B2C3B]' : 'bg-transparent text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}
            >
              <Mail className="w-3 h-3" /> Needs email
              {needsEmailCount > 0 && (
                <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${needsEmailOnly ? 'bg-[#FBF7F2] text-[#7B2C3B]' : 'bg-[#F4E4DA] text-[#7B2C3B]'}`}>{needsEmailCount}</span>
              )}
            </button>
            <button
              data-testid="filter-opened-no-reply"
              onClick={() => setOpenedNoReplyOnly((v) => !v)}
              title="Show only guests who opened their invitation email but haven't RSVPed yet."
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1 ${openedNoReplyOnly ? 'bg-[#7B2C3B] text-[#FBF7F2] border-[#7B2C3B]' : 'bg-transparent text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}
            >
              <Eye className="w-3 h-3" /> Opened, no reply
              {openedNoReplyCount > 0 && (
                <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${openedNoReplyOnly ? 'bg-[#FBF7F2] text-[#7B2C3B]' : 'bg-[#F4E4DA] text-[#7B2C3B]'}`}>{openedNoReplyCount}</span>
              )}
            </button>
            <button
              data-testid="filter-address-missing"
              onClick={() => setAddrFilter((v) => !v)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1 ${addrFilter ? 'bg-[#7B2C3B] text-[#FBF7F2] border-[#7B2C3B]' : 'bg-transparent text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}
            >
              <MapPin className="w-3 h-3" /> Address missing
              <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${addrFilter ? 'bg-[#FBF7F2] text-[#7B2C3B]' : 'bg-[#F4E4DA] text-[#7B2C3B]'}`}>{guests.filter((g) => ['missing', 'needs_fix'].includes(addressStatus(g))).length}</span>
            </button>
            <select data-testid="filter-side" value={sideFilter} onChange={(e) => setSideFilter(e.target.value)} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] bg-transparent">
              <option value="all">All sides</option>
              <option>Bride</option><option>Groom</option><option>Both</option>
            </select>
            <select data-testid="filter-group" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] bg-transparent">
              <option value="all">All groups</option>
              <option>Family</option><option>Friends</option><option>Work</option><option>Other</option>
            </select>
            <button data-testid="guests-import-csv" onClick={() => setShowImport(true)} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] inline-flex items-center gap-1">
              <Upload className="w-3.5 h-3.5" /> Import
            </button>
            <a data-testid="guests-export-csv" href={`${process.env.REACT_APP_BACKEND_URL}/api/guests/export.csv`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] inline-flex items-center gap-1"
              onClick={async (e) => {
                // Fetch with auth header + trigger download client-side so
                // the JWT actually gets sent (target=_blank on <a> can't set headers).
                e.preventDefault();
                try {
                  const res = await client.get('/guests/export.csv', { responseType: 'blob' });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'guest-list.csv';
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                } catch (err) {
                  toast({ title: "Couldn't export", description: err?.message || 'Try again.' });
                }
              }}
            >
              <Download className="w-3.5 h-3.5" /> Export
            </a>
            <button data-testid="guests-generate-all-links" onClick={generateAllLinks} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] inline-flex items-center gap-1">
              <Link2 className="w-3.5 h-3.5" /> Generate RSVP links
            </button>
            <button data-testid="guests-quick-rsvp" onClick={() => setShowQuick(true)} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] inline-flex items-center gap-1">
              <ClipboardCheck className="w-3.5 h-3.5" /> Quick RSVP entry
            </button>
            <button data-testid="guests-request-addresses" onClick={() => setShowAddrReq(true)} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> Request addresses
            </button>
            <button onClick={addGuest} className="btn-primary text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1">
              <UserPlus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          <QuickRsvpEntry open={showQuick} onOpenChange={setShowQuick} guests={guests} onSaved={reloadGuests} toast={toast} mealOptions={mealOptions} />
          <AddressRequestDialog open={showAddrReq} onOpenChange={setShowAddrReq} guests={guests} onSent={reloadGuests} toast={toast} />
        </div>

        <PlusOneCheck guests={guests} onSet={updateGuest} />
        <div className="px-4 py-2.5 bg-[#FBF7F2] border-b border-[#EFE3D8] overflow-x-auto">
          <DeliveryLegend testid="guests-delivery-legend-top" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FBF7F2] text-[#6A6A6A]">
              <tr>
                <Th />
                <Th>
                  <span className="inline-flex items-center gap-1">
                    Name &amp; partner
                    <InfoTip testId="info-delivery-dot" title="What's the little dot next to each name?">
                      <p>It's the invitation-delivery status. Once you send from the <strong>Invitations</strong> tab, the dot fills in and updates as things happen:</p>
                      <ul className="ml-4 mt-1 space-y-1">
                        <li className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#EFC9B0] inline-block" /> <span><strong>Queued</strong> — accepted by our email service, waiting on delivery</span></li>
                        <li className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#4A7A4A] inline-block" /> <span><strong>Delivered</strong> — landed in their inbox</span></li>
                        <li className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#7B2C3B] inline-block" /> <span><strong>Opened</strong> — they read it (or at least previewed it)</span></li>
                        <li className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> <span><strong>Bounced / spam</strong> — bad address or marked as spam</span></li>
                        <li className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#A0644C] inline-block" /> <span><strong>Failed</strong> — send couldn&apos;t go through</span></li>
                      </ul>
                      <p className="mt-2">Hover the dot to see the exact status. No dot means we haven&apos;t emailed them yet.</p>
                      <p className="mt-2 text-[11px]"><strong>Only queued and never advances?</strong> Add the Postmark tracking webhook in your Postmark server (URL is shown on the Wedding dashboard's Email delivery card).</p>
                    </InfoTip>
                  </span>
                </Th>
                <Th>Side</Th>
                <Th>Group</Th>
                <Th>RSVP</Th>
                <Th>+1</Th>
                <Th title="Out of towner — gets hotel recs in invitation">Travel</Th>
                <Th title="Table number (used by the Seating tab)">Table</Th>
                <Th>Meal</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <React.Fragment key={g.id}>
                <tr data-guest-row={g.id} className={`border-t border-[#EFE3D8] hover:bg-[#FBF7F2] transition-shadow ${highlightedId === g.id ? 'ring-2 ring-[#7B2C3B] ring-offset-2 rounded-md bg-[#F4E4DA]/40' : ''}`}>
                  <Td className="w-6">
                    <button data-testid={`guest-expand-${g.id}`} onClick={() => toggleExpanded(g.id)} className="text-[#9A8A7E] hover:text-[#7B2C3B]">
                      {expanded[g.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <DeliveryDot status={g.email_status} openedAt={g.email_opened_at} guest={g} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <input data-testid={`guest-name-${g.id}`} value={g.name} onChange={(e) => updateGuest(g.id, { name: e.target.value })} className="font-medium text-[#2A2A2A] bg-transparent focus:outline-none min-w-[9rem] flex-1 basis-full sm:basis-auto" />
                          {!g.email && !!g.phone && (
                            <span
                              data-testid={`guest-needs-email-${g.id}`}
                              title="No email on file. They'll get the 'reply with your email' SMS — we'll auto-fill this once they respond."
                              className="text-[9px] uppercase tracking-wider bg-[#F4E4DA] text-[#7B2C3B] border border-[#B8896C] px-1.5 py-0.5 rounded-full whitespace-nowrap inline-flex items-center gap-1"
                            >
                              <Mail className="w-2.5 h-2.5" /> Needs email
                            </span>
                          )}
                          <AddressStatusChip guest={g} />
                          {g.has_ambiguous_reply && (
                            <span
                              data-testid={`guest-ambiguous-${g.id}`}
                              title="This guest's phone matches more than one couple on the platform. We routed the reply to you based on recency — click to review and confirm."
                              onClick={() => updateGuest(g.id, { has_ambiguous_reply: false })}
                              className="cursor-pointer text-[9px] uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded-full whitespace-nowrap inline-flex items-center gap-1 hover:bg-amber-100"
                            >
                              ⚠ Shared number · click to clear
                            </span>
                          )}
                        </div>
                        {g.plusOne && (
                          <div className="mt-1 text-[11px] text-[#6A6A6A] flex items-center gap-1">
                            <span className="text-[#9A8A7E]">+</span>
                            <input
                              data-testid={`guest-plusonename-${g.id}`}
                              value={g.plusOneName || ''}
                              onChange={(e) => updateGuest(g.id, { plusOneName: e.target.value })}
                              placeholder="Partner's name"
                              className="bg-transparent focus:outline-none flex-1"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <select value={g.side} onChange={(e) => updateGuest(g.id, { side: e.target.value })} className="bg-transparent text-xs px-1 py-0.5">
                      <option>Bride</option><option>Groom</option><option>Both</option>
                    </select>
                  </Td>
                  <Td>
                    <select value={g.group} onChange={(e) => updateGuest(g.id, { group: e.target.value })} className="bg-transparent text-xs px-1 py-0.5">
                      <option>Family</option><option>Friends</option><option>Work</option><option>Other</option>
                    </select>
                  </Td>
                  <Td>
                    <button onClick={() => cycleRsvp(g)} className={`px-2.5 py-1 rounded-full text-xs border ${rsvpStyles[g.rsvp]}`}>{g.rsvp}</button>
                  </Td>
                  <Td>
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input data-testid={`guest-plusone-${g.id}`} type="checkbox" checked={g.plusOne} onChange={(e) => updateGuest(g.id, { plusOne: e.target.checked })} />
                    </label>
                  </Td>
                  <Td>
                    <button
                      data-testid={`guest-oot-${g.id}`}
                      onClick={() => updateGuest(g.id, { outOfTowner: !g.outOfTowner })}
                      title={g.outOfTowner ? 'Out of towner — will get hotel recs' : 'Mark as out of towner'}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border transition-colors ${g.outOfTowner ? 'bg-[#F4E4DA] border-[#B8896C] text-[#7B2C3B]' : 'bg-transparent border-[#EFE3D8] text-[#9A8A7E] hover:border-[#B8896C]'}`}
                    >
                      <Plane className="w-3 h-3" /> {g.outOfTowner ? 'OOT' : ''}
                    </button>
                  </Td>
                  <Td>
                    <input
                      data-testid={`guest-table-${g.id}`}
                      type="number"
                      min="0"
                      value={g.table || ''}
                      onChange={(e) => updateGuest(g.id, { table: e.target.value.replace(/[^0-9]/g, '') })}
                      placeholder="—"
                      title="Assign a table number — the Seating tab groups guests by this."
                      className="w-14 bg-transparent text-xs text-center rounded-md border border-[#EFE3D8] focus:outline-none focus:border-[#7B2C3B] px-1.5 py-1"
                    />
                  </Td>
                  <Td>
                    <div className="flex flex-col gap-1">
                      <MealSelect options={mealOptions} testId={`guest-meal-${g.id}`} value={g.meal} onChange={(v) => updateGuest(g.id, { meal: v })} className="w-24" />
                      {g.plusOne && isPlusOneAttending(g) && (
                        <MealSelect options={mealOptions} testId={`guest-plusone-meal-${g.id}`} value={g.plusOneMeal} onChange={(v) => updateGuest(g.id, { plusOneMeal: v })} placeholder="+1 meal" className="w-24" />
                      )}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`guest-copy-rsvp-${g.id}`}
                        onClick={() => copyRsvpLink(g)}
                        title="Copy RSVP link"
                        className="text-[#B8896C] hover:text-[#7B2C3B]"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeGuest(g.id)} className="text-[#B8896C] hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </Td>
                </tr>
                {expanded[g.id] && (
                  <tr data-testid={`guest-expand-row-${g.id}`} className="border-t border-[#EFE3D8] bg-[#FBF7F2]/60">
                    <Td />
                    <td colSpan={9} className="px-4 py-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <Field label="Relationship">
                          <select
                            data-testid={`guest-relationship-${g.id}`}
                            value={g.relationship || ''}
                            onChange={(e) => updateGuest(g.id, { relationship: e.target.value })}
                            className="w-full px-2 py-1 rounded border border-[#EFE3D8] bg-white text-xs"
                          >
                            {RELATIONSHIP_OPTIONS.map((r) => (
                              <option key={r || 'unset'} value={r}>{r || '— select —'}</option>
                            ))}
                          </select>
                        </Field>
                        <Field label={<span className="inline-flex items-center gap-1">Seating <span className="text-[9px] text-[#9A8A7E] normal-case tracking-normal">(table #, syncs with Seating tab)</span></span>}>
                          <input
                            data-testid={`guest-table-expanded-${g.id}`}
                            type="number"
                            min="0"
                            value={g.table || ''}
                            onChange={(e) => updateGuest(g.id, { table: e.target.value.replace(/[^0-9]/g, '') })}
                            placeholder="e.g. 3"
                            className="w-full px-2 py-1 rounded border border-[#EFE3D8] bg-white text-xs"
                          />
                        </Field>
                        <Field label="Email">
                          <input data-testid={`guest-email-${g.id}`} type="email" value={g.email || ''} onChange={(e) => updateGuest(g.id, { email: e.target.value })} placeholder="alex@example.com" className="w-full px-2 py-1 rounded border border-[#EFE3D8] bg-white text-xs" />
                        </Field>
                        <Field label="Phone">
                          <input data-testid={`guest-phone-${g.id}`} type="tel" value={g.phone || ''} onChange={(e) => updateGuest(g.id, { phone: e.target.value })} placeholder="909-268-7272" className="w-full px-2 py-1 rounded border border-[#EFE3D8] bg-white text-xs" />
                        </Field>
                        <Field label="Mailing address">
                          <AddressFields guest={g} onSaved={(patch) => setGuests((prev) => prev.map((x) => x.id === g.id ? { ...x, ...patch } : x))} />
                        </Field>
                        <Field label="Dietary">
                          <input data-testid={`guest-dietary-${g.id}`} value={g.dietary || ''} onChange={(e) => updateGuest(g.id, { dietary: e.target.value })} placeholder="Nut allergy" className="w-full px-2 py-1 rounded border border-[#EFE3D8] bg-white text-xs" />
                        </Field>
                        <Field label="Notes">
                          <input data-testid={`guest-notes-${g.id}`} value={g.notes || ''} onChange={(e) => updateGuest(g.id, { notes: e.target.value })} placeholder="Loud music please" className="w-full px-2 py-1 rounded border border-[#EFE3D8] bg-white text-xs" />
                        </Field>
                      </div>
                      <div className="mt-4 pt-3 border-t border-dashed border-[#EFE3D8] flex items-center gap-3 flex-wrap">
                        <DeliveryLegend compact testid={`guest-delivery-legend-${g.id}`} />
                        <button
                          data-testid={`log-rsvp-${g.id}`}
                          onClick={() => setLogRsvpGuest(g)}
                          className="text-xs text-[#7B2C3B] hover:underline inline-flex items-center gap-1"
                          title="Manually accept or decline for this guest — for calls, texts, in-person Yes/Nos"
                        >
                          <ClipboardCheck className="w-3 h-3" /> Manually Accept/Decline for a Guest
                        </button>
                        <details data-testid={`guest-message-history-toggle-${g.id}`} className="w-full">
                          <summary className="text-xs text-[#7B2C3B] cursor-pointer hover:underline">Message history</summary>
                          <div className="mt-2 max-h-72 overflow-auto"><MessageLog guestId={g.id} compact /></div>
                        </details>
                        <ConsentProofButton testId={`guest-consent-pdf-${g.id}`} path={`/guests/${g.id}/consent-proof.pdf`} filename={`consent-${(g.name || 'guest').replace(/\W+/g, '-').toLowerCase()}.pdf`} toast={toast} />
                        <LateInviteControls guest={g} toast={toast} onChanged={(patch) => setGuests((prev) => prev.map((x) => x.id === g.id ? { ...x, ...patch } : x))} />
                      </div>
                      {(g.rsvp_source || (g.rsvp_manual_log && g.rsvp_manual_log.length > 0)) && (
                        <div data-testid={`rsvp-audit-${g.id}`} className="mt-3 p-3 rounded-xl bg-white border border-[#EFE3D8]">
                          <p className="text-[10px] uppercase tracking-wider text-[#7B2C3B] mb-1">RSVP audit</p>
                          <ul className="space-y-1 text-[11px] text-[#3A3A3A]">
                            {(g.rsvp_manual_log || []).slice(-5).reverse().map((e, i) => (
                              <li key={i}>
                                <strong>{e.rsvp}</strong> · {SOURCE_LABEL[e.source] || e.source}
                                {e.sourceOther ? ` (${e.sourceOther})` : ''}
                                {' · '}<span className="text-[#9A8A7E]">{fmtAuditTs(e.at)}</span>
                                {e.note && <div className="text-[#6A6A6A]">"{e.note}"</div>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <DeliveryTimeline guestId={g.id} tz={profileTz} />
                      <ReminderSchedule key={`${g.id}-${g.rsvp}`} guestId={g.id} tz={profileTz} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-10 text-[#6A6A6A]">{guests.length === 0 ? 'No guests yet — click “Add” to get started.' : 'No matches.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showImport && (
        <CsvImportModal
          existingGuests={guests}
          onClose={() => setShowImport(false)}
          onImported={async (result) => {
            const { data } = await client.get('/guests');
            setGuests(data);
            toast({
              title: 'Import complete',
              description: `${result.created} added · ${result.updated} updated · ${result.skipped} skipped`,
            });
            setShowImport(false);
          }}
        />
      )}
      {logRsvpGuest && (
        <LogRsvpModal
          guest={logRsvpGuest}
          onClose={() => setLogRsvpGuest(null)}
          onSaved={async (updated) => {
            setGuests((prev) => prev.map((g) => (g.id === updated.id ? { ...g, ...updated } : g)));
            toast({ title: 'RSVP logged', description: `${updated.name}: ${updated.rsvp} · ${SOURCE_LABEL[updated.rsvp_source] || updated.rsvp_source}` });
            setLogRsvpGuest(null);
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent, 'data-testid': testid }) {
  return (
    <div data-testid={testid} className={`soft-card rounded-2xl p-4 ${accent ? 'bg-[#F6EAE0]' : ''}`}>
      <p className="text-xs text-[#6A6A6A]">{label}</p>
      <p className="font-serif text-2xl text-[#2A2A2A] mt-1">{value}</p>
      {sub ? <p className="text-[10px] text-[#9A8A7E] mt-1">{sub}</p> : null}
    </div>
  );
}
function Th({ children, title }) { return <th title={title} className="text-left font-medium px-4 py-3">{children}</th>; }
function Td({ children, className = '' }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">{label}</label>
      {children}
    </div>
  );
}

const DELIVERY_LABEL = {
  submitted: 'Invite queued — waiting on carrier',
  delivered: 'Invite delivered',
  opened: 'Invite opened',
  bounced: 'Invite bounced — email address may be wrong',
  spam_complaint: 'Marked as spam',
  failed: 'Send failed',
};
const DELIVERY_COLOR = {
  submitted: 'bg-[#EFC9B0]',
  delivered: 'bg-[#4A7A4A]',
  opened: 'bg-[#7B2C3B]',
  bounced: 'bg-red-500',
  spam_complaint: 'bg-red-500',
  failed: 'bg-[#A0644C]',
};

// Format a stored ISO-with-Z timestamp into a friendly local time string,
// e.g. "Feb 26, 2026 at 2:03 PM". Returns '' on missing/invalid values so
// callers can conditionally render.
export function fmtDeliveryTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const dateStr = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    const timeStr = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${dateStr} at ${timeStr}`;
  } catch { return ''; }
}

// Build a multi-line tooltip showing every delivery event we have on
// record — used by both the row-level dot AND the Recipient Manager pill.
export function buildDeliveryTooltip(g) {
  if (!g) return '';
  const lines = [];
  const push = (label, iso) => {
    const s = fmtDeliveryTime(iso);
    if (s) lines.push(`${label}: ${s}`);
  };
  push('Email queued', g.email_sent_at);
  push('Email delivered', g.email_delivered_at || (g.email_status === 'delivered' ? g.email_status_at || g.email_last_event_at : null));
  push('Email opened', g.email_opened_at);
  if (g.email_status === 'bounced' || g.email_status === 'spam_complaint') {
    push(g.email_status === 'bounced' ? 'Email bounced' : 'Spam complaint', g.email_status_at || g.email_last_event_at);
  }
  if (g.email_status === 'failed') push('Email failed', g.email_status_at || g.email_last_event_at);
  push('Text queued', g.sms_sent_at || g.sms_last_attempt_at);
  push('Text delivered', g.sms_delivered_at || (g.sms_status === 'delivered' ? g.sms_status_at || g.sms_last_attempt_at : null));
  if (g.sms_status === 'failed') push('Text failed', g.sms_status_at || g.sms_last_attempt_at);
  push('RSVP submitted', g.rsvp_submitted_at);
  return lines.join('\n');
}

function DeliveryDot({ status, openedAt, guest }) {
  // Prefer 'opened' if we have an opened_at even if status hasn't been overwritten
  const effective = openedAt ? 'opened' : status;
  if (!effective || !DELIVERY_COLOR[effective]) return <span className="w-2 h-2 inline-block" aria-hidden="true" />;
  const label = DELIVERY_LABEL[effective] || effective;
  const detail = buildDeliveryTooltip(guest || {});
  const title = detail ? `${label}\n\n${detail}` : label;
  return (
    <span
      title={title}
      data-testid={`delivery-dot-${effective}`}
      className={`w-2 h-2 rounded-full inline-block flex-shrink-0 ${DELIVERY_COLOR[effective]}`}
    />
  );
}

// Shared decoder used at the top of the Guests table and inside the expanded row.
// Keep this the single source of truth for delivery colors so nothing drifts.
const DELIVERY_LEGEND = [
  { key: 'none',           color: 'border border-[#D9CCBE] bg-transparent', label: 'Not sent yet' },
  { key: 'submitted',      color: 'bg-[#EFC9B0]',                            label: 'Queued' },
  { key: 'delivered',      color: 'bg-[#4A7A4A]',                            label: 'Delivered' },
  { key: 'opened',         color: 'bg-[#7B2C3B]',                            label: 'Opened' },
  { key: 'bounced',        color: 'bg-red-500',                              label: 'Bounced / spam' },
  { key: 'failed',         color: 'bg-[#A0644C]',                            label: 'Failed' },
];

function DeliveryLegend({ compact = false, testid = 'delivery-legend' }) {
  return (
    <div
      data-testid={testid}
      className={`flex items-center gap-x-4 gap-y-2 flex-wrap ${compact ? 'text-[11px]' : 'text-xs'} text-[#6A6A6A]`}
    >
      <span className={`font-medium text-[#7B2C3B] ${compact ? 'text-[11px]' : 'text-xs'}`}>Dot legend</span>
      {DELIVERY_LEGEND.map((item) => (
        <span
          key={item.key}
          data-testid={`delivery-legend-${item.key}`}
          className="inline-flex items-center gap-1.5"
        >
          <span className={`w-2 h-2 rounded-full inline-block ${item.color}`} />
          <span>{item.label}</span>
        </span>
      ))}
    </div>
  );
}


// -----------------------------------------------------------------------------
// CSV Import Modal — three-step wizard:
//   1) Pick file / paste text (Papaparse handles quoted commas, etc.)
//   2) Show preview + duplicate warnings (name-based, case-insensitive)
//      → user picks Skip / Overwrite / Create for each duplicate
//   3) POST /api/guests/bulk-import
// The parent Guests component refreshes the list on success.
// -----------------------------------------------------------------------------
const CSV_COLUMN_ALIASES = {
  name: 'name',
  'guest name': 'name',
  'first name': 'name',
  fullname: 'name',
  'full name': 'name',
  partner: 'plusOneName',
  'partner name': 'plusOneName',
  'plus one name': 'plusOneName',
  // "+1" / "plus one" (yes/no) → boolean flag on the guest
  '+1': 'plusOne',
  '+1?': 'plusOne',
  'plus one': 'plusOne',
  'plus-one': 'plusOne',
  'plusone': 'plusOne',
  'has plus one': 'plusOne',
  'has +1': 'plusOne',
  email: 'email',
  'e-mail': 'email',
  phone: 'phone',
  'phone number': 'phone',
  mobile: 'phone',
  address: 'address',
  street: 'address',
  side: 'side',
  group: 'group',
  category: 'group',
  relationship: 'relationship',
  relation: 'relationship',
  table: 'table',
  'table number': 'table',
  'table #': 'table',
  seating: 'table',
  meal: 'meal',
  'meal choice': 'meal',
  dietary: 'dietary',
  'dietary restrictions': 'dietary',
  allergies: 'dietary',
  notes: 'notes',
  'out-of-towner': 'outOfTowner',
  'out of towner': 'outOfTowner',
  oot: 'outOfTowner',
  outoftowner: 'outOfTowner',
  travelling: 'outOfTowner',
};

const normalizeHeader = (h) => (h || '').toString().trim().toLowerCase();

const parseCsvRow = (row, headers) => {
  const out = {};
  headers.forEach((h, i) => {
    const key = CSV_COLUMN_ALIASES[normalizeHeader(h)];
    if (!key) return;
    let val = (row[i] ?? '').toString().trim();
    if (key === 'outOfTowner') {
      out.outOfTowner = /^(y|yes|true|1|oot)$/i.test(val);
      return;
    }
    if (key === 'plusOne') {
      out.plusOne = /^(y|yes|true|1|\+1)$/i.test(val);
      return;
    }
    if (key === 'side') {
      const s = val.toLowerCase();
      out.side = s.startsWith('b') && !s.startsWith('both') ? 'Bride' :
                 s.startsWith('g') ? 'Groom' :
                 s.startsWith('both') || s === 'shared' ? 'Both' : 'Bride';
      return;
    }
    if (key === 'group') {
      const g = val.toLowerCase();
      out.group = g.startsWith('fam') ? 'Family' :
                  g.startsWith('fri') ? 'Friends' :
                  g.startsWith('work') || g.startsWith('col') ? 'Work' :
                  val || 'Other';
      return;
    }
    if (key === 'table') {
      out.table = val.replace(/[^0-9]/g, '');
      return;
    }
    out[key] = val;
  });
  // If a Partner name was provided, imply plusOne=true so the seating/RSVP
  // logic actually shows the +1 (unless the sheet explicitly set +1=N).
  if (out.plusOneName && out.plusOne === undefined) {
    out.plusOne = true;
  }
  return out;
};

function CsvImportModal({ existingGuests, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [rawText, setRawText] = useState('');
  const [parsed, setParsed] = useState([]);  // [{ raw, guest, isDup }]
  const [dupChoices, setDupChoices] = useState({});  // idx -> 'skip'|'overwrite'|'create'
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);

  const existingNames = useMemo(() => {
    const s = new Set();
    (existingGuests || []).forEach((g) => {
      if (g.name) s.add(g.name.trim().toLowerCase());
    });
    return s;
  }, [existingGuests]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setRawText(ev.target.result || '');
    };
    reader.readAsText(file);
  };

  const parseNow = () => {
    setError('');
    if (!rawText.trim()) {
      setError('Paste some CSV content or choose a file first.');
      return;
    }
    const result = Papa.parse(rawText.trim(), { skipEmptyLines: true });
    if (!result.data || result.data.length < 2) {
      setError('CSV needs at least one header row and one data row.');
      return;
    }
    const [headerRow, ...dataRows] = result.data;
    const items = dataRows
      .map((row) => parseCsvRow(row, headerRow))
      .filter((g) => (g.name || '').trim());

    if (!items.length) {
      setError('No rows had a Name column. Check that your CSV has a "Name" header.');
      return;
    }

    const enriched = items.map((g) => {
      const key = (g.name || '').trim().toLowerCase();
      return { guest: g, isDup: existingNames.has(key) };
    });

    // Default choice for each dup: skip. User picks per-row on next screen.
    const defaults = {};
    enriched.forEach((r, i) => { if (r.isDup) defaults[i] = 'skip'; });
    setDupChoices(defaults);
    setParsed(enriched);
    setStep(2);
  };

  const dupCount = parsed.filter((r) => r.isDup).length;
  const newCount = parsed.length - dupCount;

  const doImport = async () => {
    setImporting(true);
    setError('');
    try {
      const payload = parsed.map((r, i) => ({
        ...r.guest,
        onDuplicate: r.isDup ? (dupChoices[i] || 'skip') : 'create',
      }));
      const { data } = await client.post('/guests/bulk-import', { guests: payload });
      onImported(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Import failed.');
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await client.get('/guests/template.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'guest-list-template.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="csv-import-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-[#EFE3D8] flex items-start justify-between gap-3">
          <div>
            <h2 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#7B2C3B]" /> Import guests from CSV
            </h2>
            <p className="text-xs text-[#6A6A6A] mt-0.5">Step {step} of 2 — {step === 1 ? 'pick your file' : 'confirm the preview'}</p>
          </div>
          <button data-testid="csv-import-close" onClick={onClose} className="text-[#9A8A7E] hover:text-[#7B2C3B] p-1"><X className="w-5 h-5" /></button>
        </div>

        {step === 1 && (
          <div className="p-5 overflow-y-auto space-y-4">
            <div className="rounded-xl bg-[#FBF7F2] border border-[#EFE3D8] p-4">
              <p className="text-sm text-[#2A2A2A] font-medium mb-1">How it works</p>
              <ol className="text-xs text-[#6A6A6A] space-y-1 list-decimal ml-4">
                <li>Open Google Sheets (or Excel) and use these column headers exactly, in any order:
                  <div className="mt-1 flex flex-wrap gap-1">
                    {['Name', 'Partner name', '+1', 'Email', 'Phone', 'Address', 'Side', 'Group', 'Relationship', 'Table', 'Meal', 'Dietary', 'Notes', 'Out-of-towner'].map((h) => (
                      <code key={h} className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-[#EFE3D8]">{h}</code>
                    ))}
                  </div>
                </li>
                <li>Fill one guest per row. Only <strong>Name</strong> is required — everything else is optional.</li>
                <li><strong>Side</strong> → Bride, Groom or Both · <strong>Group</strong> → Family, Friends, Work, Other · <strong>+1</strong> → Y or N · <strong>Out-of-towner</strong> → Y or N</li>
                <li>Tip: If you fill in <em>Partner name</em> we&apos;ll auto-flag +1 as Y so you can skip that column.</li>
                <li>In Google Sheets: <em>File → Download → CSV</em>, then drop the file below.</li>
              </ol>
              <button
                data-testid="csv-download-template"
                onClick={downloadTemplate}
                className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2]"
              >
                <Download className="w-3.5 h-3.5" /> Download blank template
              </button>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Upload CSV file</label>
              <input
                data-testid="csv-file-input"
                type="file"
                accept=".csv,text/csv"
                onChange={handleFile}
                className="text-xs"
              />
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">…or paste CSV text</label>
              <textarea
                data-testid="csv-paste-area"
                rows={6}
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder={"Name,Email,Side,Group,Table\nAlex Rivera,alex@example.com,Bride,Friends,5"}
                className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-xs font-mono focus:outline-none focus:border-[#7B2C3B]"
              />
            </div>

            {error && <p data-testid="csv-import-error" className="text-xs text-red-700 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {error}</p>}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#EFE3D8]">
              <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">Cancel</button>
              <button data-testid="csv-parse-btn" onClick={parseNow} className="btn-primary text-xs px-4 py-1.5 rounded-full">Preview →</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="p-4 border-b border-[#EFE3D8] bg-[#FBF7F2] flex items-center gap-3 text-xs text-[#2A2A2A] flex-wrap">
              <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3.5 h-3.5" /> {newCount} new</span>
              {dupCount > 0 && (
                <span className="inline-flex items-center gap-1 text-[#A0644C]"><AlertTriangle className="w-3.5 h-3.5" /> {dupCount} duplicate{dupCount === 1 ? '' : 's'} — pick an action for each</span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <table className="w-full text-xs">
                <thead className="text-[#6A6A6A] bg-[#FBF7F2] sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-2">Name</th>
                    <th className="text-left px-2 py-2">Side</th>
                    <th className="text-left px-2 py-2">Group</th>
                    <th className="text-left px-2 py-2">Table</th>
                    <th className="text-left px-2 py-2">Relationship</th>
                    <th className="text-left px-2 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.map((r, i) => (
                    <tr key={i} data-testid={`csv-preview-row-${i}`} className={`border-t border-[#EFE3D8] ${r.isDup ? 'bg-[#FFF7EC]' : ''}`}>
                      <td className="px-2 py-2 font-medium">{r.guest.name}</td>
                      <td className="px-2 py-2 text-[#6A6A6A]">{r.guest.side || '—'}</td>
                      <td className="px-2 py-2 text-[#6A6A6A]">{r.guest.group || '—'}</td>
                      <td className="px-2 py-2 text-[#6A6A6A]">{r.guest.table || '—'}</td>
                      <td className="px-2 py-2 text-[#6A6A6A]">{r.guest.relationship || '—'}</td>
                      <td className="px-2 py-2">
                        {r.isDup ? (
                          <select
                            data-testid={`csv-dup-choice-${i}`}
                            value={dupChoices[i] || 'skip'}
                            onChange={(e) => setDupChoices((p) => ({ ...p, [i]: e.target.value }))}
                            className="text-xs px-2 py-1 rounded border border-[#EFC9B0] bg-white"
                          >
                            <option value="skip">Skip (keep existing)</option>
                            <option value="overwrite">Overwrite existing</option>
                            <option value="create">Add as new duplicate</option>
                          </select>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3 h-3" /> new</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {error && <p className="px-4 py-2 text-xs text-red-700 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {error}</p>}
            <div className="p-4 border-t border-[#EFE3D8] flex items-center justify-between gap-2">
              <button onClick={() => setStep(1)} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">← Back</button>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] hover:border-[#B8896C]">Cancel</button>
                <button
                  data-testid="csv-import-confirm"
                  onClick={doImport}
                  disabled={importing || parsed.length === 0}
                  className="btn-primary text-xs px-4 py-1.5 rounded-full disabled:opacity-60 inline-flex items-center gap-1"
                >
                  {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  Import {parsed.length} row{parsed.length === 1 ? '' : 's'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// DeliveryTimeline — inline widget in the expanded guest row. Lazy-loads
// /api/invitations/timeline/:guestId, then renders each event as a
// horizontal step (queued → delivered → opened) or as an error state.
// Doesn't render at all when the guest has no send history yet.
// -----------------------------------------------------------------------------
function DeliveryTimeline({ guestId, tz }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get(`/invitations/timeline/${guestId}`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [guestId]);

  if (state.loading) return null;
  if (state.error || !state.data) return null;
  const events = state.data.events || [];
  if (events.length === 0) {
    return (
      <div data-testid={`delivery-timeline-${guestId}`} className="mt-3 pt-3 border-t border-[#EFE3D8]">
        <p className="text-[11px] uppercase tracking-wider text-[#9A8A7E] mb-1">Delivery timeline</p>
        <p className="text-xs text-[#9A8A7E] italic">No invitations sent yet.</p>
      </div>
    );
  }

  // Format an event to a tone + label
  const dot = (kind) => ({
    send: { color: '#EFC9B0', label: 'Queued' },
    delivered: { color: '#4A7A4A', label: 'Delivered' },
    opened: { color: '#7B2C3B', label: 'Opened' },
    bounced: { color: '#DC2626', label: 'Bounced' },
    spam_complaint: { color: '#DC2626', label: 'Spam' },
    failed: { color: '#A0644C', label: 'Failed' },
  })[kind] || { color: '#9A8A7E', label: kind };

  const fmt = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      // Format in the couple's Settings-defined timezone (falls back to
      // the browser's) and include the tz abbreviation so it's obvious.
      // "Feb 25, 10:22 AM PST"
      const opts = {
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
        timeZoneName: 'short',
      };
      if (tz) opts.timeZone = tz;
      return d.toLocaleString(undefined, opts);
    } catch { return iso; }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#EFE3D8]" data-testid={`delivery-timeline-${guestId}`}>
      <p className="text-[11px] uppercase tracking-wider text-[#9A8A7E] mb-2">Delivery timeline</p>
      <ol className="flex flex-wrap items-start gap-x-2 gap-y-2">
        {events.map((e, i) => {
          const d = dot(e.kind === 'send' ? (e.status === 'failed' ? 'failed' : 'send') : e.kind);
          const iconLabel = e.channel === 'sms' ? 'SMS' : 'Email';
          return (
            <li key={i} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: d.color }} />
              <span className="text-[11px] text-[#3A3A3A]">
                <strong>{d.label}</strong>
                <span className="text-[#9A8A7E]"> · {iconLabel}{e.is_nudge ? ' (nudge)' : ''}{e.at ? ` · ${fmt(e.at)}` : ''}</span>
              </span>
              {i < events.length - 1 && <span className="text-[#DAC7B8]">›</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}



// Log-an-RSVP modal — for calls, texts, in-person Yes/Nos. Fires the same
// downstream triggers as a form-submitted RSVP (confirmation email/SMS to
// the guest + notification SMS to the couple).
function LogRsvpModal({ guest, onClose, onSaved }) {
  const [rsvp, setRsvp] = useState('Yes');
  const [source, setSource] = useState('called');
  const [sourceOther, setSourceOther] = useState('');
  const [note, setNote] = useState('');
  const [notifyGuest, setNotifyGuest] = useState(true);
  const [saving, setSaving] = useState(false);
  const SOURCES = [
    { value: 'called',    label: 'Called us',    icon: Phone },
    { value: 'texted',    label: 'Texted us',    icon: MessageSquare },
    { value: 'emailed',   label: 'Emailed us',   icon: Mail },
    { value: 'in_person', label: 'Told us in person', icon: MapPin },
    { value: 'other',     label: 'Other',        icon: FileText },
  ];
  const save = async () => {
    setSaving(true);
    try {
      const payload = { rsvp, source, sourceOther: source === 'other' ? sourceOther : null, note, notifyGuest };
      const { data } = await client.post(`/guests/${guest.id}/manual-rsvp`, payload);
      onSaved(data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="log-rsvp-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between">
          <h3 className="font-serif text-xl">Manually Accept/Decline for {guest.name}</h3>
          <button onClick={onClose} className="text-[#6A6A6A] hover:text-[#2A2A2A]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#7B2C3B] mb-2">Response</p>
            <div className="flex items-center gap-2">
              <button data-testid="log-rsvp-yes" onClick={() => setRsvp('Yes')} className={`px-4 py-2 rounded-full border ${rsvp==='Yes' ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'}`}>❤️ Yes</button>
              <button data-testid="log-rsvp-no" onClick={() => setRsvp('No')} className={`px-4 py-2 rounded-full border ${rsvp==='No' ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'}`}>👎 No</button>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#7B2C3B] mb-2">How did they respond?</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SOURCES.map((s) => {
                const Icon = s.icon;
                return (
                  <button key={s.value} data-testid={`log-rsvp-src-${s.value}`} onClick={() => setSource(s.value)} className={`px-3 py-2 rounded-full border text-xs inline-flex items-center gap-1.5 justify-center ${source===s.value ? 'bg-[#F4E4DA] text-[#7B2C3B] border-[#B8896C]' : 'bg-white border-[#EFE3D8] text-[#3A3A3A]'}`}>
                    <Icon className="w-3.5 h-3.5" /> {s.label}
                  </button>
                );
              })}
            </div>
            {source === 'other' && (
              <input
                data-testid="log-rsvp-src-other"
                value={sourceOther}
                onChange={(e) => setSourceOther(e.target.value)}
                placeholder="e.g. via my mom"
                className="mt-2 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]"
              />
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#7B2C3B] mb-2">Optional note</p>
            <textarea
              data-testid="log-rsvp-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. prefers fish, walker for aisle, +1 Susan"
              className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-[#3A3A3A]">
            <input data-testid="log-rsvp-notify" type="checkbox" className="accent-[#7B2C3B]" checked={notifyGuest} onChange={(e) => setNotifyGuest(e.target.checked)} />
            <span>Also send the guest the same confirmation email / text they'd get from the form</span>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-[#EFE3D8] bg-[#FBF7F2] flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8] text-[#3A3A3A]">Cancel</button>
          <button data-testid="log-rsvp-save" onClick={save} disabled={saving || (source === 'other' && !sourceOther.trim())} className="text-sm px-5 py-2 rounded-full btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} Save RSVP
          </button>
        </div>
      </div>
    </div>
  );
}

// Compact per-guest reminder schedule. Lazy-loads on expand so a huge
// guest list doesn't fire N fetches at once. Renders each chain / chase
// step with the exact wall-clock time it will fire (or already did).
function ReminderSchedule({ guestId, tz }) {
  const [state, setState] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await client.get(`/reminders/schedule-preview/${guestId}`);
        if (alive) setState(data);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [guestId]);

  const fmt = (iso) => {
    if (!iso) return '';
    try {
      // Explicit year + numeric hour/minute + timeZone name so couples
      // can never confuse "Oct 15" this year vs. next, and see a clear
      // AM/PM. Example: "Oct 15, 2026 · 9:00 AM PDT"
      const opts = {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZoneName: 'short',
      };
      if (tz) opts.timeZone = tz;
      return new Date(iso).toLocaleString(undefined, opts).replace(', ', ' · ');
    } catch { return iso; }
  };

  const [firing, setFiring] = React.useState({});
  const fireNow = async (stepId) => {
    if (firing[stepId]) return;
    if (!window.confirm('Send this reminder to the guest right now? This bypasses the scheduler and cannot be undone.')) return;
    setFiring((prev) => ({ ...prev, [stepId]: true }));
    try {
      await client.post(`/reminders/chain/${guestId}/${stepId}/fire-now`);
      // Optimistically flip local state → "Sent"
      setState((prev) => prev && ({
        ...prev,
        steps: prev.steps.map((s) => s.id === stepId
          ? { ...s, status: 'fired', fired_at: new Date().toISOString() }
          : s),
      }));
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Send failed. Try again.');
    } finally {
      setFiring((prev) => ({ ...prev, [stepId]: false }));
    }
  };

  if (loading) return null;
  if (!state || !state.steps || state.steps.length === 0) return null;

  const STATUS_MAP = {
    fired: { color: '#4A7A4A', label: 'Sent', dot: '#4A7A4A' },
    scheduled: { color: '#7B2C3B', label: 'Scheduled', dot: '#EFC9B0' },
    due_now: { color: '#A0644C', label: 'Due now — will send this hour', dot: '#A0644C' },
    disabled: { color: '#9A8A7E', label: 'Disabled', dot: '#D4C4B8' },
    no_phone: { color: '#9A8A7E', label: 'No phone on file', dot: '#D4C4B8' },
    skipped_no_anchor: { color: '#9A8A7E', label: 'Waiting on a date', dot: '#D4C4B8' },
    skipped_no_invitation: { color: '#9A8A7E', label: 'Invitation not sent yet', dot: '#D4C4B8' },
    skipped_rsvpd: { color: '#4A7A4A', label: 'Guest already replied', dot: '#4A7A4A' },
    skipped_reply_by_passed: { color: '#A0644C', label: 'Reply-by passed', dot: '#A0644C' },
  };

  return (
    <div className="mt-3 pt-3 border-t border-dashed border-[#EFE3D8]" data-testid={`reminder-schedule-${guestId}`}>
      <div className="text-[11px] uppercase tracking-widest text-[#7B2C3B] mb-2">Reminder Schedule</div>
      <div className="text-[11px] text-[#6A6A6A] mb-2">
        {state.mode === 'chain' ? 'Multi-reminder chain' : 'Single chase mode'}
        {' · '}fires at {state.send_window?.start} local ({state.timezone})
      </div>
      <ul className="space-y-1.5">
        {state.steps.map((s) => {
          const meta = STATUS_MAP[s.status] || STATUS_MAP.disabled;
          const when = s.status === 'fired' ? fmt(s.fired_at) : fmt(s.fire_at);
          const canFireNow = s.status === 'due_now' || s.status === 'scheduled';
          return (
            <li key={s.id} className="text-xs flex items-start gap-2" data-testid={`reminder-step-${s.id}`}>
              <span className="mt-1 inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: meta.dot }} />
              <div className="flex-1 min-w-0">
                <div>
                  <span className="font-medium text-[#2A2A2A]">Reminder #{s.index}</span>
                  {' · '}<span className="text-[#6A6A6A]">{s.label}</span>
                </div>
                <div className="text-[11px]" style={{ color: meta.color }}>
                  {meta.label}{when ? ` · ${when}` : ''}
                </div>
                {s.message_preview && (
                  <div className="text-[11px] text-[#9A8A7E] italic truncate">"{s.message_preview}"</div>
                )}
              </div>
              {canFireNow && (
                <button
                  data-testid={`fire-now-${s.id}`}
                  onClick={() => fireNow(s.id)}
                  disabled={!!firing[s.id]}
                  className="text-[10px] px-2 py-1 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white disabled:opacity-50 flex-shrink-0"
                  title="Send this reminder to the guest right now (bypasses the scheduler)"
                >{firing[s.id] ? 'Sending…' : 'Send now'}</button>
              )}
            </li>
          );
        })}
      </ul>
      {state.notes && state.notes.length > 0 && (
        <div className="mt-2 text-[11px] text-[#9A8A7E] space-y-0.5">
          {state.notes.map((n, i) => (<div key={i}>{n}</div>))}
        </div>
      )}
    </div>
  );
}

